import { useMediaQuery } from "@mui/system";
import {
  ArrayField,
  ChipField,
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  ShowButton,
  SimpleList,
  SingleFieldList,
  TextField,
  useListContext,
  usePermissions,
} from "react-admin";

import { settings } from "app";
import permissions from "app/permissions";
import { administrationRequests } from "api/administrationApi";
import { erasmusRequests } from "api/erasmusApi";
import { MobileListRow } from "features/MobileListRow";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import { ErasmusInternshipStatus, MOBILE_BREAKPOINT } from "utils/constants";
import CoordinatorApproveDialog from "./components/CoordinatorApproveDialog";
import CoordinatorArriveDialog from "./components/CoordinatorArriveDialog";
import CoordinatorFinishDialog from "./components/CoordinatorFinishDialog";
import CoordinatorRejectDialog from "./components/CoordinatorRejectDialog";

const listFilters = [
  <ReferenceInput
    key="status"
    source="status"
    reference={erasmusRequests.getInternshipStatusesSelect()}
    alwaysOn
  >
    <SelectInput
      label="Статус"
      optionText="nameBg"
    />
  </ReferenceInput>,
];

const REJECTABLE_STATUSES = [ErasmusInternshipStatus.New, ErasmusInternshipStatus.CoordinatorApproved];

const IncomingInternshipDatagrid = () => {
  const { data } = useListContext();
  const { permissions: userPermissions } = usePermissions();

  // Every action has its own column, so a column is rendered only when at least one row on the page
  // can actually use it - otherwise a header would stand above an entirely empty column.
  const canManage = checkPermission(userPermissions, permissions.canManageIncomingInternshipStudentsCoordinator);
  const showApprove = canManage && data?.some(record => record?.internshipStatus === ErasmusInternshipStatus.New);
  const showReject = canManage && data?.some(record => REJECTABLE_STATUSES.includes(record?.internshipStatus));
  const showArrive = canManage && data?.some(record => record?.internshipStatus === ErasmusInternshipStatus.PrintedLoA);
  const showFinish = canManage && data?.some(record => record?.internshipStatus === ErasmusInternshipStatus.Arrived);

  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick={false}
      sx={settings.style.firstExpandIconDatagrid}
    >
      <TextField
        label="Име"
        source="firstName"
        sortable={false}
        emptyText="-"
      />
      <TextField
        label="Фамилия"
        source="lastName"
        sortable={false}
        emptyText="-"
      />
      <ReferenceField
        label="Университет"
        source="universityId"
        reference={administrationRequests.getUniversitiesSelect()}
        sortable={false}
        emptyText="-"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <ArrayField
        label="Стажове"
        source="internships"
        sortable={false}
        emptyText="-"
      >
        <SingleFieldList linkType={false}>
          <ChipField
            source="name"
            emptyText="-"
          />
        </SingleFieldList>
      </ArrayField>
      <ReferenceField
        label="Статус"
        source="internshipStatus"
        reference={erasmusRequests.getInternshipStatusesSelect()}
        sortable={false}
        emptyText="-"
        link={false}
      >
        <TextField source="nameBg" />
      </ReferenceField>
      {showApprove ? (
        <FunctionField
          label=""
          sortable={false}
          render={record =>
            record?.internshipStatus === ErasmusInternshipStatus.New ? (
              <CoordinatorApproveDialog
                id={record.id}
                internships={record.internships}
              />
            ) : null
          }
        />
      ) : null}
      {showReject ? (
        <FunctionField
          label=""
          sortable={false}
          render={record =>
            REJECTABLE_STATUSES.includes(record?.internshipStatus) ? (
              <CoordinatorRejectDialog
                id={record.id}
                internships={record.internships}
              />
            ) : null
          }
        />
      ) : null}
      {showArrive ? (
        <FunctionField
          label=""
          sortable={false}
          render={record =>
            record?.internshipStatus === ErasmusInternshipStatus.PrintedLoA ? (
              <CoordinatorArriveDialog
                id={record.id}
                internships={record.internships}
              />
            ) : null
          }
        />
      ) : null}
      {showFinish ? (
        <FunctionField
          label=""
          sortable={false}
          render={record =>
            record?.internshipStatus === ErasmusInternshipStatus.Arrived ? (
              <CoordinatorFinishDialog id={record.id} />
            ) : null
          }
        />
      ) : null}
      <ShowButton />
    </Datagrid>
  );
};

const ListIncomingStudentsIntership = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <List
      title="Списък пристигащи на стаж"
      sx={settings.style.blockStyle}
      filters={listFilters}
      exporter={false}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <MobileListRow
              fields={[
                {
                  label: "Имена",
                  value: (
                    <>
                      {record?.firstName} {record?.lastName}
                    </>
                  ),
                },
              ]}
            />
          )}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[
                  {
                    label: "Университет",
                    value: (
                      <ReferenceField
                        source="universityId"
                        reference={administrationRequests.getUniversitiesSelect()}
                        emptyText="-"
                        link={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                  {
                    label: "Стажове",
                    value: (
                      <ArrayField
                        source="internships"
                        emptyText="-"
                      >
                        <SingleFieldList linkType={false}>
                          <ChipField
                            source="name"
                            emptyText="-"
                          />
                        </SingleFieldList>
                      </ArrayField>
                    ),
                  },
                  {
                    label: "Статус",
                    value: (
                      <ReferenceField
                        source="internshipStatus"
                        reference={erasmusRequests.getInternshipStatusesSelect()}
                        emptyText="-"
                        link={false}
                      >
                        <TextField source="nameBg" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {record?.internshipStatus === ErasmusInternshipStatus.New ? (
                  <SecuredComponent permission={permissions.canManageIncomingInternshipStudentsCoordinator}>
                    <CoordinatorApproveDialog
                      id={record.id}
                      internships={record.internships}
                    />
                  </SecuredComponent>
                ) : null}
                {REJECTABLE_STATUSES.includes(record?.internshipStatus) ? (
                  <SecuredComponent permission={permissions.canManageIncomingInternshipStudentsCoordinator}>
                    <CoordinatorRejectDialog
                      id={record.id}
                      internships={record.internships}
                    />
                  </SecuredComponent>
                ) : null}
                {record?.internshipStatus === ErasmusInternshipStatus.PrintedLoA ? (
                  <SecuredComponent permission={permissions.canManageIncomingInternshipStudentsCoordinator}>
                    <CoordinatorArriveDialog
                      id={record.id}
                      internships={record.internships}
                    />
                  </SecuredComponent>
                ) : null}
                {record?.internshipStatus === ErasmusInternshipStatus.Arrived ? (
                  <SecuredComponent permission={permissions.canManageIncomingInternshipStudentsCoordinator}>
                    <CoordinatorFinishDialog id={record.id} />
                  </SecuredComponent>
                ) : null}
                <ShowButton />
              </div>
            </div>
          )}
        />
      ) : (
        <IncomingInternshipDatagrid />
      )}
    </List>
  );
};

export default ListIncomingStudentsIntership;
